// src/pages/Presentation/SmartVideoPlayerAdmin.tsx
import React, {
  useRef,
  useEffect,
  useState,
  memo,
  CSSProperties,
  useMemo,
} from 'react';
import { useTranslation } from "react-i18next";
import Hls from 'hls.js';

interface SmartVideoPlayerGalleryProps {
  videoUrl: string;
  style?: CSSProperties;
  muted?: boolean;
  /** Prefer false in feed, true in overlay. */
  showControls?: boolean;
  /** Enable click-to-toggle when native controls are hidden. */
  clickToToggle?: boolean;
  onMutedChange?: (muted: boolean) => void; // NEW
  preload?: 'none' | 'metadata' | 'auto';
}

function inferMime(url: string): string | undefined {
  const q = url.split('?')[0].toLowerCase();
  if (q.endsWith('.mp4')) return 'video/mp4';
  if (q.endsWith('.webm')) return 'video/webm';
  if (q.endsWith('.mov')) return 'video/quicktime';
  if (q.endsWith('.m4v')) return 'video/x-m4v';
  return undefined;
}

function isHlsUrl(url: string): boolean {
  const q = url.split('?')[0].toLowerCase();
  return q.endsWith('.m3u8') || q.includes('/hls/playlist/');
}

const SmartVideoPlayerGallery: React.FC<SmartVideoPlayerGalleryProps> = ({
  videoUrl,
  style,
  muted = true,
  showControls = true,   // overlay-friendly default
  clickToToggle = true,  // feed-friendly default
  onMutedChange,
  preload = 'metadata',
}) => {
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const wasMutedRef = useRef<boolean>(!!muted); // NEW

  const [isVisible, setIsVisible] = useState(false);
  const [userPaused, setUserPaused] = useState(false);

  // Hover/tap-driven controls visibility
  const [isHover, setIsHover] = useState(false);
  const hoverHideTimer = useRef<number | null>(null);

  // Only change sizing for landscape videos
  const [isLandscape, setIsLandscape] = useState<boolean | null>(null);

  const type = useMemo(() => inferMime(videoUrl), [videoUrl]);

  // Derived: only show native controls when hovered OR playing
const controlsActive = showControls && isHover;

  // Preload
  const effectivePreload = isVisible
    ? (preload ?? 'auto')
    : (preload ?? 'metadata');
  // ——— Visibility observer (preload ahead) ———

useEffect(() => {
  const video = videoRef.current;
  if (!video || !videoUrl) return;

  let hls: Hls | null = null;
  const hlsSource = isHlsUrl(videoUrl);

  if (hlsSource) {
    if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = videoUrl;
      video.load();
    } else if (Hls.isSupported()) {
      hls = new Hls({
        enableWorker: true,
        startLevel: 0,
      });

      hls.loadSource(videoUrl);
      hls.attachMedia(video);

      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        if (isVisible && !userPaused) {
          video.play().catch(() => {});
        }
      });
    } else {
      video.src = videoUrl;
      video.load();
    }
  } else {
    video.src = videoUrl;
    video.load();
  }

  return () => {
    try {
      if (hls) hls.destroy();
      video.pause();
      video.removeAttribute('src');
      video.load();
    } catch {}
  };
}, [videoUrl, isVisible, userPaused]);


  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => setIsVisible(entry.isIntersecting),
      { root: null, rootMargin: '800px 0px', threshold: 0.1 }
    );
    obs.observe(el);
    return () => obs.unobserve(el);
  }, []);

  // ——— Pause on tab hidden; resume only if not user-paused ———
  useEffect(() => {
    const onVis = () => {
      const v = videoRef.current;
      if (!v) return;
      if (document.hidden) {
        v.pause();
      } else if (isVisible && !userPaused) {
        v.play().catch(() => {});
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [isVisible, userPaused]);

  // ——— Autoplay when visible; pause when not (respect userPaused) ———
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (isVisible) {
      try { v.preload = effectivePreload; } catch {}
      if (!userPaused) v.play().catch(() => {});
    } else {
      v.pause();
    }
  }, [isVisible, videoUrl, userPaused, effectivePreload]);

  useEffect(() => {
    if (videoRef.current) videoRef.current.muted = !!muted;
    wasMutedRef.current = !!muted;
  }, [muted]);

  // ——— Reset per-source, cleanup buffer ———
  useEffect(() => {
  setUserPaused(false);
  setIsLandscape(null);
}, [videoUrl]);

  // ——— Hover/touch handling for controls visibility ———
  const scheduleHide = (ms = 1200) => {
    if (hoverHideTimer.current) window.clearTimeout(hoverHideTimer.current);
    hoverHideTimer.current = window.setTimeout(() => setIsHover(false), ms);
  };
  useEffect(() => {
    return () => {
      if (hoverHideTimer.current) window.clearTimeout(hoverHideTimer.current);
    };
  }, []);

  // ——— Overlay click to toggle (only when native controls are hidden) ———
  const onOverlayClick = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      v.play().then(() => setUserPaused(false)).catch(() => {});
    } else {
      v.pause();
      setUserPaused(true);
    }
  };

  

  // Style: only adjust for landscape; otherwise use your original sizing
  const videoStyle: CSSProperties =
    isLandscape
      ? {
          // Landscape → shrink-to-fit width, keep inside container
          width: '100%',
          height: 'auto',
          maxHeight: '100%',
          maxWidth: '100%',
          objectFit: 'contain',
          backgroundColor: '#000',
        }
      : {
          // Portrait/Square/Unknown → original behavior
          height: '100%',
          width: 'auto',
          objectFit: 'contain',
          backgroundColor: '#000',
        };

  return (
    <div
      ref={containerRef}
      style={{
        position: 'relative',
        height: '80vh',
        width: 'auto',
        maxWidth: '95vw',
        borderRadius: 12,
        backgroundColor: '#000',
        overflow: 'hidden',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        ...style,
      }}
      onMouseEnter={() => {
        if (hoverHideTimer.current) window.clearTimeout(hoverHideTimer.current);
        setIsHover(true);
      }}
      onMouseLeave={() => {
        // Hide controls after a short delay if paused; hide immediately if playing
        if (userPaused) scheduleHide(400);
        else setIsHover(false);
      }}
      onTouchStart={() => {
        // Mobile: brief reveal of controls
        setIsHover(true);
        scheduleHide(2000);
      }}
    >
<video
  key={videoUrl}
  ref={videoRef}
  muted={muted}
  playsInline
  controls={controlsActive}
  preload={effectivePreload}
  controlsList="nodownload"
  style={videoStyle}
  onLoadedMetadata={(e) => {
    const v = e.currentTarget as HTMLVideoElement;
    const w = v.videoWidth || 0;
    const h = v.videoHeight || 0;
    if (w && h) setIsLandscape(w > h);
  }}
  onLoadedData={(e) => {
    if (isLandscape === null) {
      const v = e.currentTarget as HTMLVideoElement;
      const w = v.videoWidth || 0;
      const h = v.videoHeight || 0;
      if (w && h) setIsLandscape(w > h);
    }
  }}
  onPlay={() => setUserPaused(false)}
  onPause={() => {}}
  onVolumeChange={() => {
    const v = videoRef.current;
    if (!v) return;
    if (v.muted !== wasMutedRef.current) {
      wasMutedRef.current = v.muted;
      onMutedChange?.(v.muted);
    }
  }}
  onError={(e) => {
    console.warn('Video failed to play:', videoUrl, e);
  }}
>
  {t("smartVideo.html5Fallback")}
</video>

      {/* Click overlay: only when we want click-to-toggle and native controls are NOT visible */}
      {clickToToggle && !controlsActive && (
        <div
          onClick={onOverlayClick}
          style={{
            position: 'absolute',
            inset: 0,
            background: 'transparent',
            cursor: 'pointer',
          }}
        />
      )}
    </div>
  );
};

export default memo(SmartVideoPlayerGallery);
